import { merge as observableMerge, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { Component, Injector, OnDestroy } from '@angular/core';
import { CoreService } from './core/store/core.service';
import { LoginService } from './login/store/login.service';
import { AlertMessageComponent } from './alert/alert.component';
import { Event, NavigationStart, Router } from '@angular/router';
import { ReefKeepAliveService } from '@reef/core';
import { AppService } from './application/app.service';
import { AlertListObject } from './alert/alert.datamodel';
import { PermissionsService } from './user/permissions/permissions.service';
import { SsoService } from './sso/sso.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

@Component({
  selector: "ama-ng-app-store",
  template: `
    <ama-ng-app-store-toast-notifications></ama-ng-app-store-toast-notifications>
    <ama-ng-layout></ama-ng-layout>
  `,
  standalone: false
})
export class AppComponent implements OnDestroy {
  private readonly subscribers: Subscription[] = [];

  private connected = false;

  constructor(
    private readonly coreService: CoreService,
    private readonly permissionsService: PermissionsService,
    readonly loginService: LoginService,
    private readonly reefKeepAliveService: ReefKeepAliveService,
    private readonly modalService: NgbModal,
    readonly appService: AppService,
    private readonly router: Router,
    private readonly ssoService: SsoService
  ) {
    this.subscribers.push(
      router.events
        .pipe(filter((event) => event instanceof NavigationStart))
        .subscribe((event: Event) => {
            if ((event as NavigationStart).restoredState) {
              this.navigateTo(
                  (event as NavigationStart).url.split('/')[1].split('?')[0],
                  true
              );
            }
          }
        )
    );
    this.subscribers.push(
      observableMerge(
        this.coreService.errors$,
        this.loginService.errorsOnLogout$,
        this.appService.infoOnAppInstall$
      ).subscribe(newErrors => {
        this.displayOnAlertMessage(newErrors);
      })
    );
    this.subscribers.push(
      this.loginService.isConnected$.subscribe(connected => {
        this.connected = connected;
        if (connected) {
          this.reefKeepAliveService.start();
          this.permissionsService.getUserPermissions();
        } else {
          this.reefKeepAliveService.stop();
        }
      })
    );
    this.subscribers.push(
      this.coreService.navigation$.subscribe(newNavigation => {
        const alert = newNavigation.navigationBlocked;
        if (
          alert.targetSection !== undefined &&
          Object.keys(alert).length !== 0
        ) {
          const newAlert = Object.assign({}, alert);
          newAlert.actions = alert.actions.slice();
          const discardAction = Object.assign({}, newAlert.actions[0]);
          const previousCallback = discardAction.callback;
          discardAction.callback = () => {
            previousCallback(discardAction.options);
            this.navigateTo(alert.targetSection);
          };
          newAlert.actions[0] = discardAction;
          this.coreService.displayAlert(newAlert);
        } else if (!newNavigation.isRestoredState && (!this.ssoService.isSSOInPath() || this.connected)) {
            this.router.navigate([newNavigation.section]).then(() => {
          });
        }
      })
    );
    this.startLoginFlow();
  }

  startLoginFlow() {
    if (this.ssoService.isSSOLoginFlow()) {
      this.ssoService.loginBySSO();
    } else if (this.isPasswordResetFlow()) {
      this.loginService.displayLoginPage();
    } else {
      this.loginService.checkLoginRequired();
    }
  }

  ngOnDestroy() {
    this.subscribers.forEach(it => it.unsubscribe());
  }

  navigateTo = (destination: string, isRestoredState = false) => this.coreService.navigateTo(destination, isRestoredState);


  /**
   * Display infos on an Alert popup.
   * @param newInfos
   */
  private displayOnAlertMessage(newInfos: AlertListObject) {
    if (newInfos.alerts != null && newInfos.alerts.length !== 0) {
      const SHOULD_LOG_USER_OUT = newInfos.shouldLogout;
      const modalRef = this.modalService.open(AlertMessageComponent, { size: 'xl',
        injector: Injector.create({
          providers: [
            { provide: AlertListObject, useValue: newInfos },
          ]
        })});
      if (SHOULD_LOG_USER_OUT) {
        modalRef.result.then(
          result => this.loginService.afterLogOut(),
          reason => this.loginService.afterLogOut()
        );
      }
    }
  }

  /**
   * Check whether the user has opened a password reset link
   */
  private isPasswordResetFlow(): boolean {
    return window.location.search.indexOf("ACTION=validate") !== -1;
  }
}
